import { ChartData, ChartOptions, Plugin, TooltipItem } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import chroma from 'chroma-js';
import IconButton from 'components/IconButton';
import React, { useMemo } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import { CardBody, CardHeader, CardTitle } from 'reactstrap';

import {
  BAR_CHART_OPTIONS,
  DOUGHNUT_CHART_OPTIONS,
} from '../components/BarOrDoughnutChart/options';
import { ChartType } from '../types';

interface KPIBarOrDoughnutChartProps {
  barDatasetColors?: (string | string[])[];
  chartType: ChartType.Bar | ChartType.Doughnut;
  colors: string[];
  containerClassName?: string;
  datasets: number[][];
  headerActions?: React.ReactNode;
  isActive: boolean;
  labels: string[];
  onExpandClick: () => void;
  summaryValue?: number;
  title: string;
  tooltipLabels: string[];
  valueFormatter: (value: number) => string;
}

const DoughnutChartDataLabels = ChartDataLabels as Plugin<'doughnut'>;

const getBarOptions = (
  valueFormatter: (value: number) => string,
): ChartOptions<'bar'> => ({
  ...BAR_CHART_OPTIONS,
  plugins: {
    ...BAR_CHART_OPTIONS.plugins,
    datalabels: {
      ...BAR_CHART_OPTIONS.plugins?.datalabels,
      formatter: (value) => valueFormatter(Number(value)),
    },
    tooltip: {
      ...BAR_CHART_OPTIONS.plugins?.tooltip,
      callbacks: {
        ...BAR_CHART_OPTIONS.plugins?.tooltip?.callbacks,
        label: (context: TooltipItem<'bar'>) =>
          [
            context.dataset.label,
            context.label,
            valueFormatter(Number(context.raw || 0)),
          ]
            .filter(Boolean)
            .join(': '),
      },
    },
  },
});

const getDoughnutOptions = (
  valueFormatter: (value: number) => string,
): ChartOptions<'doughnut'> => ({
  ...DOUGHNUT_CHART_OPTIONS,
  plugins: {
    ...DOUGHNUT_CHART_OPTIONS.plugins,
    datalabels: {
      ...DOUGHNUT_CHART_OPTIONS.plugins?.datalabels,
      formatter: (value) =>
        Number(value) === 0 ? '' : valueFormatter(Number(value)),
    },
    tooltip: {
      ...DOUGHNUT_CHART_OPTIONS.plugins?.tooltip,
      callbacks: {
        ...DOUGHNUT_CHART_OPTIONS.plugins?.tooltip?.callbacks,
        label: (context: TooltipItem<'doughnut'>) =>
          [
            context.dataset.label,
            context.label,
            valueFormatter(Number(context.raw || 0)),
          ]
            .filter(Boolean)
            .join(': '),
      },
    },
  },
});

const KPIBarOrDoughnutChart = ({
  barDatasetColors,
  chartType,
  colors,
  containerClassName,
  datasets,
  headerActions,
  isActive,
  labels,
  onExpandClick,
  summaryValue,
  title,
  tooltipLabels,
  valueFormatter,
}: KPIBarOrDoughnutChartProps) => {
  const chartId = title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const displayLabels = useMemo(
    () =>
      labels.map((label) =>
        label.length > 50 ? `${label.substring(0, 50)}...` : label,
      ),
    [labels],
  );
  const barData = useMemo<ChartData<'bar'>>(
    () => ({
      labels: displayLabels,
      datasets: datasets.map((dataset, index) => {
        const backgroundColor = barDatasetColors?.[index] || colors[index];
        const hoverBorderColor = Array.isArray(backgroundColor)
          ? backgroundColor.map((color) => chroma(color).darken(2).hex())
          : chroma(backgroundColor).darken(2).hex();
        return {
          label: tooltipLabels[index],
          data: dataset,
          fill: true,
          backgroundColor,
          hoverBorderColor,
        };
      }),
    }),
    [barDatasetColors, colors, datasets, displayLabels, tooltipLabels],
  );
  const doughnutData = useMemo<ChartData<'doughnut'>>(
    () => ({
      labels: displayLabels,
      datasets: [
        {
          label: tooltipLabels[0],
          data: datasets[0] || [],
          backgroundColor: colors,
          hoverBorderColor: chroma(colors[0]).darken(2).hex(),
        },
      ],
    }),
    [colors, datasets, displayLabels, tooltipLabels],
  );
  const barOptions = useMemo(
    () => getBarOptions(valueFormatter),
    [valueFormatter],
  );
  const doughnutOptions = useMemo(
    () => getDoughnutOptions(valueFormatter),
    [valueFormatter],
  );
  const renderChart = () => (
    <div className="chart-area">
      {chartType === ChartType.Doughnut ? (
        <Doughnut
          data={doughnutData}
          options={doughnutOptions}
          plugins={[DoughnutChartDataLabels]}
        />
      ) : (
        <Bar data={barData} options={barOptions} plugins={[ChartDataLabels]} />
      )}
    </div>
  );

  return (
    <article
      className={`card-chart border border-light ${containerClassName || ''}`}
    >
      <CardHeader>
        <CardTitle tag="div" className="mb-0">
          <div className="d-flex flex-row align-items-center justify-content-between">
            <div className="d-flex flex-row align-items-baseline">
              {summaryValue !== undefined && (
                <h1 className="d-inline mb-0">{summaryValue}</h1>
              )}
              <h5 className="card-category px-1">{title}</h5>
            </div>
            <div className="d-flex align-items-center">
              {headerActions}
              <IconButton
                id={`expand-kpi-${chartId}`}
                active={isActive}
                iconClass={
                  isActive
                    ? 'bi bi-arrows-angle-contract'
                    : 'bi bi-arrows-angle-expand'
                }
                size="sm"
                tooltip={isActive ? `Contract ${title}` : `Expand ${title}`}
                onClick={onExpandClick}
              />
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardBody style={{ cursor: 'pointer' }}>{renderChart()}</CardBody>
    </article>
  );
};

export default KPIBarOrDoughnutChart;
